import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Oval } from 'react-loader-spinner'
import Swal from 'sweetalert2'

import { wizardStep } from 'service/EmployeeWizardService'
import { PageHeader, UnvButton } from 'components'

const STEPS = ['Личное', 'Должность', 'Доступы', 'Финал']

const ROLES = [
  { value: 'teacher', label: 'Преподаватель' },
  { value: 'methodist', label: 'Методист' },
  { value: 'head_of_department', label: 'Зав. кафедрой' },
  { value: 'dean', label: 'Декан' },
  { value: 'accountant', label: 'Бухгалтер' },
  { value: 'archivist', label: 'Архивариус' },
  { value: 'hr_students', label: 'ОК-студенты' },
  { value: 'hr_employees', label: 'ОК-сотрудники' },
]

const generatePassword = () => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#'
  let s = ''
  for (let i = 0; i < 12; i++) s += chars[Math.floor(Math.random() * chars.length)]
  return s
}

const EmployeeWizardPage = () => {
  const { t } = useTranslation('common')

  const navigate = useNavigate()
  const [activeStep, setActiveStep] = useState(0)
  const [wizardId, setWizardId] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [data, setData] = useState({
    last_name: '',
    first_name: '',
    middle_name: '',
    passport_series: '',
    passport_number: '',
    inn: '',
    phone: '',
    department: '',
    position: '',
    rate: 1,
    start_date: '',
    email: '',
    temp_password: generatePassword(),
    role: 'teacher',
  })

  const setField = (k) => (e) => setData({ ...data, [k]: e.target.value })

  const submitStep = async (stepNumber, payload) => {
    setBusy(true)
    setError('')
    try {
      const { data: res } = await wizardStep({
        step: stepNumber,
        wizard_id: wizardId,
        ...payload,
      })
      if (res?.wizard_id) setWizardId(res.wizard_id)
      return res
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Не удалось сохранить шаг'
      setError(msg)
      throw e
    } finally {
      setBusy(false)
    }
  }

  const handleNext = async () => {
    try {
      if (activeStep === 0) {
        const { last_name, first_name, passport_series, passport_number, inn, phone, middle_name } = data
        if (!last_name || !first_name) {
          setError('Заполните ФИО')
          return
        }
        await submitStep(1, { last_name, first_name, middle_name, passport_series, passport_number, inn, phone })
      } else if (activeStep === 1) {
        const { department, position, rate, start_date } = data
        if (!department || !position || !start_date) {
          setError('Заполните кафедру, позицию и дату начала')
          return
        }
        await submitStep(2, { department, position, rate, start_date })
      } else if (activeStep === 2) {
        const { email, temp_password, role } = data
        if (!email || !temp_password) {
          setError('Email и временный пароль обязательны')
          return
        }
        await submitStep(3, { email, temp_password, role })
      }
      setActiveStep((s) => s + 1)
      setError('')
    } catch {
      
    }
  }

  const handleSubmit = async () => {
    try {
      await submitStep(4, { commit: true })
      Swal.fire({
        title: 'Сотрудник принят',
        icon: 'success',
        timer: 2500,
        showConfirmButton: false,
      })
      navigate('/employees/')
    } catch {
      
    }
  }

  const handleCopy = (text) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text)
    Swal.fire({ icon: 'success', title: 'Скопировано', timer: 1200, showConfirmButton: false, toast: true, position: 'top-end' })
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <PageHeader
        title="Приём сотрудника"
        subtitle="Пошаговое оформление нового сотрудника"
      />
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Stepper activeStep={activeStep} sx={{ my: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t('user.last_name')} value={data.last_name} onChange={setField('last_name')} required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t('user.first_name')} value={data.first_name} onChange={setField('first_name')} required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t('user.middle_name')} value={data.middle_name} onChange={setField('middle_name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t('pages.employeeWizard.passport_series')} value={data.passport_series} onChange={setField('passport_series')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t('pages.employeeWizard.passport_number')} value={data.passport_number} onChange={setField('passport_number')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t('auth.inn')} value={data.inn} onChange={setField('inn')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t('common.phone')} value={data.phone} onChange={setField('phone')} />
            </Grid>
          </Grid>
        )}

        {activeStep === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t('pages.employeeWizard.department')} value={data.department} onChange={setField('department')} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t('archive.col_position')} value={data.position} onChange={setField('position')} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label={t('employees.rate')} value={data.rate} onChange={setField('rate')} inputProps={{ step: 0.25, min: 0.25, max: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label={t('pages.employeeWizard.start_date')} InputLabelProps={{ shrink: true }} value={data.start_date} onChange={setField('start_date')} required />
            </Grid>
          </Grid>
        )}

        {activeStep === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="email" label="Email" value={data.email} onChange={setField('email')} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('pages.employeeWizard.temp_password')}
                value={data.temp_password}
                onChange={setField('temp_password')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setData({ ...data, temp_password: generatePassword() })} title={t('pages.usersTab.generate')}>
                        <RefreshIcon />
                      </IconButton>
                      <IconButton onClick={() => handleCopy(data.temp_password)} title={t('pages.employeeWizard.copy')}>
                        <ContentCopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label={t('teacherDash.role')} value={data.role} onChange={setField('role')}>
                {ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        )}

        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>Проверьте данные</Typography>
            <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 1 }}>
              <dt><b>{t('pages.employeeWizard.fio_lbl')}</b></dt>
              <dd>{data.last_name} {data.first_name} {data.middle_name}</dd>
              <dt><b>{t('pages.employeeWizard.passport_lbl')}</b></dt>
              <dd>{data.passport_series} {data.passport_number}</dd>
              <dt><b>{t('pages.employeeWizard.inn_lbl')}</b></dt>
              <dd>{data.inn}</dd>
              <dt><b>{t('pages.employeeWizard.phone_lbl')}</b></dt>
              <dd>{data.phone}</dd>
              <dt><b>{t('pages.employeeWizard.department_lbl')}</b></dt>
              <dd>{data.department}</dd>
              <dt><b>{t('pages.employeeWizard.position_lbl')}</b></dt>
              <dd>{data.position} ({data.rate} ст.)</dd>
              <dt><b>{t('pages.employeeWizard.start_date_lbl')}</b></dt>
              <dd>{data.start_date}</dd>
              <dt><b>Email:</b></dt>
              <dd>{data.email}</dd>
              <dt><b>{t('pages.employeeWizard.role_lbl')}</b></dt>
              <dd>{ROLES.find((r) => r.value === data.role)?.label}</dd>
            </Box>
          </Box>
        )}

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button disabled={activeStep === 0 || busy} onClick={() => setActiveStep((s) => s - 1)}>
            {t('common.back', 'Назад')}
          </Button>
          {activeStep < STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={busy}
              startIcon={busy ? <Oval height={16} width={16} color="#fff" secondaryColor="#ffe0c2" /> : null}
              sx={{ bgcolor: '#FF7A00', '&:hover': { bgcolor: '#e96d00' } }}
            >
              {t('common.next', 'Далее')}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={busy}
              startIcon={busy ? <Oval height={16} width={16} color="#fff" secondaryColor="#ffe0c2" /> : null}
              sx={{ bgcolor: '#1A8754', '&:hover': { bgcolor: '#146944' } }}
            >
              {t('hr.wizard.hire', 'Принять на работу')}
            </Button>
          )}
        </Stack>
      </Paper>
    </Container>
  )
}

export default EmployeeWizardPage
